import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import { RoofFramingType, type RoofTruss } from "building-types/framing/roofFramingTypes";
import { RoofTrussType, type RoofTrussBasicInput } from "building-types/framing/roofTrussTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { lumberPiecesByProfileNew } from "doc/utils/lumberTools";
import { planeNormal } from "geom/plane";
import type { TrussGenerator } from "./trussGenerator";
import {
  generateBottomChord as generateBottomAndHeelChords,
  generateTopChords,
  projectChordsToFramingPlane,
  setupBottomChordRails,
  setupTopChordRails,
  setupWebChordRails,
} from "./trussUtils";

export class ChordTrussGenerator implements TrussGenerator {
  generate(input: RoofTrussBasicInput): RoofTruss {
    const {
      leftSpan,
      leftEaveFasciaSize,
      rightSpan,
      rightEaveFasciaSize,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordProfile,
      bottomChordProfile,
      bottomChordOffset,
      webChordProfile,
      trussPlane,
    } = input;

    if (Array.isArray(leftSpan) || Array.isArray(rightSpan)) {
      throw new Error("Chord truss does not support multiple spans");
    }

    const topChordHeight = unsignedNumberNew(LumberProfileCatalog[topChordProfile].width);
    const bottomChordHeight = unsignedNumberNew(LumberProfileCatalog[bottomChordProfile].width);

    const { left, right } = generateTopChords(leftSpan, rightSpan, leftTopChordPitch, rightTopChordPitch);
    const {
      bottomChord: bottomChordTopPoints,
      leftHeelChord,
      rightHeelChord,
    } = generateBottomAndHeelChords(
      leftSpan,
      rightSpan,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordHeight,
      bottomChordHeight,
      bottomChordOffset,
    );
    const [
      leftTopChordTopPointsToFramingPlane,
      rightTopChordTopPointsToFramingPlane,
      bottomChordTopPointsToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
    ] = projectChordsToFramingPlane(
      [left, right, bottomChordTopPoints, leftHeelChord, rightHeelChord],
      input.trussPlane,
    );

    const topChords = lumberPiecesByProfileNew(LumberProfileCatalog[topChordProfile], { [topChordProfile]: [] });
    const bottomChords = lumberPiecesByProfileNew(LumberProfileCatalog[bottomChordProfile], {
      [bottomChordProfile]: [],
    });
    const webChords = lumberPiecesByProfileNew(LumberProfileCatalog[webChordProfile], { [webChordProfile]: [] });
    const [normX, normY, normZ] = planeNormal(input.trussPlane);

    setupTopChordRails(topChords, leftTopChordTopPointsToFramingPlane, leftEaveFasciaSize, normX, normY, normZ);
    setupTopChordRails(topChords, rightTopChordTopPointsToFramingPlane, rightEaveFasciaSize, normX, normY, normZ);
    setupBottomChordRails(bottomChords, bottomChordTopPointsToFramingPlane, normX, normY, normZ);
    setupWebChordRails(webChords, leftHeelChordToFramingPlane, normX, normY, normZ);
    setupWebChordRails(webChords, rightHeelChordToFramingPlane, normX, normY, normZ);

    return {
      trussType: RoofTrussType.CHORD_TRUSS,
      components: [
        { type: RoofFramingComponentType.TOP_CHORD, lumber: topChords },
        { type: RoofFramingComponentType.BOTTOM_CHORD, lumber: bottomChords },
        { type: RoofFramingComponentType.WEB, lumber: webChords },
      ],
      framingType: RoofFramingType.TRUSS,
      plane: trussPlane,
    };
  }
}
